/**
 * hsp-delivery-promise.js
 *
 * Renders "order within Xh Ym and it ships today, arriving <range>" above Add to
 * Cart. Config comes from section settings via data attributes on the host
 * element - see snippets/hsp-delivery-promise.liquid.
 *
 * THINGS THIS DELIBERATELY DOES NOT DO
 *
 * 1. It never shows a stock count. This store does not track inventory
 *    (inventory_management is null on every variant), so "Only 6 left" would be
 *    a number with nothing behind it.
 * 2. It never promises a single delivery date, only a range. The store controls
 *    its dispatch cutoff; it does not control the carrier.
 * 3. It evaluates the cutoff in the WAREHOUSE timezone, never the shopper's.
 *    A shopper in London at 23:00 must see the same cutoff a shopper in Denver
 *    sees, because it is the warehouse clock that decides whether the box goes
 *    out today. Reading the browser clock's local date would make the promise
 *    wrong for everyone outside one timezone.
 *
 * Calendar arithmetic is done in UTC-midnight space. Stepping a local Date by
 * 86400000ms lands an hour off across a DST boundary; stepping a UTC midnight
 * never does, and we only ever need whole days.
 */

const WEEKDAY_SETS = {
  mon_fri: [1, 2, 3, 4, 5],
  mon_sat: [1, 2, 3, 4, 5, 6],
  all: [0, 1, 2, 3, 4, 5, 6],
};

const DAY_MS = 86400000;

/**
 * Reads wall-clock fields as they appear in a given IANA timezone.
 * @param {Date} date
 * @param {string} timeZone
 */
function zonedParts(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(date);

  const out = {};
  for (const { type, value } of parts) out[type] = value;

  return {
    year: Number(out.year),
    month: Number(out.month),
    day: Number(out.day),
    // Intl can emit hour "24" for midnight under hour12:false.
    hour: Number(out.hour) % 24,
    minute: Number(out.minute),
  };
}

/** @param {number} ms UTC-midnight timestamp @param {number[]} businessDays */
function isBusinessDay(ms, businessDays) {
  return businessDays.includes(new Date(ms).getUTCDay());
}

/** Advance to the next business day strictly after `ms`. */
function nextBusinessDay(ms, businessDays) {
  let cursor = ms + DAY_MS;
  for (let guard = 0; guard < 30; guard++) {
    if (isBusinessDay(cursor, businessDays)) return cursor;
    cursor += DAY_MS;
  }
  return cursor;
}

/** Advance `n` business days from `ms` (not counting `ms` itself). */
function addBusinessDays(ms, n, businessDays) {
  let cursor = ms;
  let remaining = n;
  for (let guard = 0; guard < 200 && remaining > 0; guard++) {
    cursor = nextBusinessDay(cursor, businessDays);
    remaining--;
  }
  return cursor;
}

function formatDay(ms) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(new Date(ms));
}

class HspDeliveryPromise extends HTMLElement {
  #timer = null;

  connectedCallback() {
    this.render();
    // Once a minute is enough for a minute-resolution countdown, and it keeps
    // the element from crossing the cutoff while someone reads the page.
    this.#timer = setInterval(() => this.render(), 60000);
  }

  disconnectedCallback() {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = null;
  }

  render() {
    const target = this.querySelector('[data-hsp-delivery-text]');
    if (!target) return;

    const tz = this.dataset.timezone;
    const businessDays = WEEKDAY_SETS[this.dataset.businessDays] || WEEKDAY_SETS.mon_fri;
    const cutoffHour = Number(this.dataset.cutoffHour);
    const cutoffMinute = Number(this.dataset.cutoffMinute);
    const transitMin = Number(this.dataset.transitMin);
    const transitMax = Number(this.dataset.transitMax);

    if (!tz || !Number.isFinite(cutoffHour) || !Number.isFinite(transitMin) || !Number.isFinite(transitMax)) {
      this.hidden = true;
      return;
    }

    let now;
    try {
      now = zonedParts(new Date(), tz);
    } catch {
      // An unrecognised timezone must fail silently rather than render a wrong
      // promise computed against the browser's own clock.
      this.hidden = true;
      return;
    }

    const todayUTC = Date.UTC(now.year, now.month - 1, now.day);
    const minutesNow = now.hour * 60 + now.minute;
    const minutesCutoff = cutoffHour * 60 + cutoffMinute;

    const shipsToday = isBusinessDay(todayUTC, businessDays) && minutesNow < minutesCutoff;
    const shipDay = shipsToday ? todayUTC : nextBusinessDay(todayUTC, businessDays);

    const arriveFrom = addBusinessDays(shipDay, transitMin, businessDays);
    const arriveTo = addBusinessDays(shipDay, transitMax, businessDays);

    let lead;
    if (shipsToday) {
      const left = minutesCutoff - minutesNow;
      const hours = Math.floor(left / 60);
      const mins = left % 60;
      const within = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
      lead = `<strong>Order within ${within}</strong> and it ships today`;
    } else {
      lead = `<strong>Ships ${formatDay(shipDay)}</strong>`;
    }

    const window =
      arriveFrom === arriveTo
        ? `Estimated delivery ${formatDay(arriveFrom)}`
        : `Estimated delivery ${formatDay(arriveFrom)} &ndash; ${formatDay(arriveTo)}`;

    target.innerHTML = `${lead}. ${window}.`;
    this.hidden = false;
  }
}

if (!customElements.get('hsp-delivery-promise')) {
  customElements.define('hsp-delivery-promise', HspDeliveryPromise);
}
